/**
 * The shared shell of the date and time popovers (STYLE_GUIDE "Date and time pickers", B-153):
 * a kg-card panel 6 px under its pill, kept 8 px inside the viewport, closed by Esc or a click
 * outside, which returns focus to the pill. Focus moving elsewhere closes it too; a resize re-places it.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

interface PickerPopoverProps {
  anchor: React.RefObject<HTMLElement>;
  label: string;
  onClose: () => void;
  children: React.ReactNode;
}

export const PickerPopover: React.FC<PickerPopoverProps> = ({ anchor, label, onClose, children }) => {
  const ref = useRef<HTMLDivElement>(null);

  const place = useCallback(() => {
    const el = ref.current, a = anchor.current;
    if (!el || !a) return;
    const ar = a.getBoundingClientRect();
    const parent = (el.offsetParent as HTMLElement | null)?.getBoundingClientRect() ?? { left: 0, top: 0 };
    const left = Math.max(8, Math.min(ar.left, window.innerWidth - 8 - el.offsetWidth));
    el.style.left = `${left - parent.left}px`;
    el.style.top = `${ar.bottom + 6 - parent.top}px`;
  }, [anchor]);
  useLayoutEffect(place, [place]);

  useEffect(() => {
    const close = (returnFocus: boolean) => {
      onClose();
      if (returnFocus) anchor.current?.focus();
    };
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!ref.current?.contains(t) && !anchor.current?.contains(t)) close(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close(true);
      }
    };
    // Non-modal: Tab past the last control closes it rather than leaving it open over the bar.
    const onFocusIn = (e: FocusEvent) => {
      const t = e.target as Node;
      if (!ref.current?.contains(t) && !anchor.current?.contains(t)) close(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('focusin', onFocusIn);
    };
  }, [anchor, onClose]);

  // A resize (zoom, a window drag) re-places it rather than leaving it misplaced.
  useEffect(() => {
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [place]);

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={label}
      className="kg-picker absolute z-50 rounded-[12px] p-3"
      style={{ background: 'var(--kg-card)', border: '1px solid var(--kg-line)', boxShadow: 'var(--kg-shadow), 0 12px 32px -18px rgba(40, 25, 15, 0.35)', color: 'var(--kg-ink)' }}
    >
      {children}
    </div>
  );
};

export default PickerPopover;
